// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { getMsalInstance } from '../context/msalInstance';
import { RootState } from './store';
import { getStoreInstance } from './storeInstance';
import { logout } from './user/userSlice';
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/',
  prepareHeaders: async (headers, api) => {
    // Backend calls carry the Microsoft Entra access token directly, acquired silently from MSAL's
    // own cache — no more Firebase-mediated session cookie (research.md Decision 1's revision
    // note).
    const instance = getMsalInstance();
    const account = instance?.getActiveAccount() ?? instance?.getAllAccounts()[0];
    const scope = (api.getState() as RootState).config.config?.msalConfig.scope;

    if (instance && account && scope) {
      try {
        const result = await instance.acquireTokenSilent({scopes: [scope], account});
        headers.set('authorization', `Bearer ${result.accessToken}`)
      } catch (error: unknown) {
        // Silent renewal failed outright (refresh token and/or the browser's own Entra SSO
        // session both expired — surfaces from MSAL as anything from InteractionRequiredAuthError
        // to a hung-iframe BrowserAuthError "timed_out"). Previously this just logged and let the
        // request go out with no Authorization header, so it 401'd with nothing telling the user
        // why or routing them back to sign in — they'd see silently broken screens, not a login
        // prompt. Dispatching logout() reuses the existing recovery path (ProtectedRoute already
        // redirects to /login whenever user is null; Login.tsx's mount effect already falls back
        // to interactive signIn() when its own acquireTokenSilent attempt fails the same way).
        console.error("acquireTokenSilent failed while preparing request headers", error);
        getStoreInstance()?.dispatch(logout());
      }
    }

    return headers
  }
})

const dynamicBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const rootState = api.getState() as RootState
  const config = rootState.config.config

  // gracefully handle scenarios where data to generate the URL is missing
  if (!config) {
    return {
      error: {
        status: 400,
        statusText: 'Bad Request',
        data: 'No project ID received',
      },
    }
  }

  const urlEnd = typeof args === 'string' ? args : args.url

  // Entra external guest user IDs end in "#EXT#@<tenant>.onmicrosoft.com" -- the literal '#' is
  // otherwise read by the URL constructor below as the start of the fragment, silently truncating
  // the rest of the path before the request is ever sent.
  const escapedUrlEnd = urlEnd.replace(/#/g, '%23')

  // construct a dynamically generated portion of the url
  const newUrl = config.apiEndpoint + escapedUrlEnd
  const adjustedUrl = new URL(newUrl, window.location.href).href

  const adjustedArgs =
      typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl }
  // provide the amended url and other params to the raw base query
  return rawBaseQuery(adjustedArgs, api, extraOptions)
}

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptySplitApi = createApi({
  baseQuery: dynamicBaseQuery,
  endpoints: () => ({}),
})
